import React, { useState, useRef, useEffect } from 'react';
import './VolumeKnob.css';

interface VolumeKnobProps {
  min?: number;
  max?: number;
  value?: number;
  onChange?: (value: number) => void;
  icon?: React.ReactNode;
}

const VolumeKnob: React.FC<VolumeKnobProps> = ({ 
  min = 0, 
  max = 100, 
  value = 50, 
  onChange,
  icon
}) => {
  const [knobValue, setKnobValue] = useState(value);
  const knobRef = useRef<HTMLDivElement>(null);
  
  // Calculate the rotation based on the current value
  const getRotation = (val: number): number => {
    // Map value range to degree range (0-270 degrees)
    return ((val - min) / (max - min)) * 270 - 135;
  };

  // Snap value to nearest 10% increment
  const snapToIncrement = (value: number): number => {
    const range = max - min;
    const increment = range * 0.1; // 10% of range
    const steps = Math.round((value - min) / increment);
    return min + (steps * increment);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!knobRef.current) return;
    e.preventDefault();
    
    // Get knob position and dimensions
    const knobRect = knobRef.current.getBoundingClientRect();
    const knobCenterX = knobRect.left + knobRect.width / 2;
    const knobCenterY = knobRect.top + knobRect.height / 2;
    
    // Calculate initial angle (in radians)
    const initialAngle = Math.atan2(e.clientY - knobCenterY, e.clientX - knobCenterX);
    let prevAngle = initialAngle;
    let currentValue = knobValue;
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      // Calculate current angle
      const currentAngle = Math.atan2(
        moveEvent.clientY - knobCenterY, 
        moveEvent.clientX - knobCenterX
      );
      
      // Calculate angle change in radians
      let deltaAngle = currentAngle - prevAngle;
      
      // Handle angle wrapping (crossing from -π to π or vice versa)
      if (deltaAngle > Math.PI) deltaAngle -= Math.PI * 2;
      if (deltaAngle < -Math.PI) deltaAngle += Math.PI * 2;
      
      // Convert to value change (2π radians = full range)
      // Rotating clockwise (positive deltaAngle) increases value
      const deltaValue = (deltaAngle / (Math.PI * 2)) * (max - min) * 2;
      
      // Update value (not snapped yet)
      let newValue = currentValue + deltaValue;
      newValue = Math.max(min, Math.min(max, newValue));
      
      // Snap value to 10% increments
      const snappedValue = snapToIncrement(newValue);
      
      setKnobValue(snappedValue);
      onChange?.(snappedValue);
      
      // Update for next move
      prevAngle = currentAngle;
      currentValue = newValue; // Store non-snapped value for smoother movement
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!knobRef.current || !e.touches[0]) return;
    e.preventDefault();
    
    // Get knob position and dimensions
    const knobRect = knobRef.current.getBoundingClientRect();
    const knobCenterX = knobRect.left + knobRect.width / 2;
    const knobCenterY = knobRect.top + knobRect.height / 2;
    
    // Calculate initial angle (in radians)
    const touch = e.touches[0];
    const initialAngle = Math.atan2(touch.clientY - knobCenterY, touch.clientX - knobCenterX);
    let prevAngle = initialAngle;
    let currentValue = knobValue;
    
    const handleTouchMove = (moveEvent: TouchEvent) => {
      if (!moveEvent.touches[0]) return;
      moveEvent.preventDefault();
      
      const touch = moveEvent.touches[0];
      
      // Calculate current angle
      const currentAngle = Math.atan2(
        touch.clientY - knobCenterY, 
        touch.clientX - knobCenterX
      );
      
      // Calculate angle change in radians
      let deltaAngle = currentAngle - prevAngle;
      
      // Handle angle wrapping (crossing from -π to π or vice versa)
      if (deltaAngle > Math.PI) deltaAngle -= Math.PI * 2;
      if (deltaAngle < -Math.PI) deltaAngle += Math.PI * 2;
      
      // Convert to value change (2π radians = full range)
      // Rotating clockwise (positive deltaAngle) increases value
      const deltaValue = (deltaAngle / (Math.PI * 2)) * (max - min) * 2;
      
      // Update value (not snapped yet)
      let newValue = currentValue + deltaValue;
      newValue = Math.max(min, Math.min(max, newValue));
      
      // Snap value to 10% increments
      const snappedValue = snapToIncrement(newValue);
      
      setKnobValue(snappedValue);
      onChange?.(snappedValue);
      
      // Update for next move
      prevAngle = currentAngle;
      currentValue = newValue; // Store non-snapped value for smoother movement
    };
    
    const handleTouchEnd = () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
    
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  };

  // Update internal value when prop changes
  useEffect(() => {
    setKnobValue(value);
  }, [value]);

  // Format the value with leading zeros
  const formattedValue = Math.round(knobValue).toString().padStart(3, '0');

  return (
    <div className="volume-knob-container">
      <div 
        className="volume-knob"
        ref={knobRef}
        style={{ transform: `rotate(${getRotation(knobValue)}deg)` }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div className="knob-indicator"></div>
        {icon && <div className="knob-icon">{icon}</div>}
      </div>
      <div className="digital-display">
        {formattedValue}
      </div>
    </div>
  );
};

export default VolumeKnob; 